/**
 * Centralized configuration management with runtime injection support
 *
 * This module reads configuration from window.__RUNTIME_CONFIG__ first (set at container startup)
 * and falls back to import.meta.env (set at build time) for development.
 *
 * This allows the same Docker image to be deployed to multiple environments
 * without rebuilding.
 *
 * All environment variables use VITE_ prefix (Vite standard).
 */

// Runtime configuration interface - all VITE_ prefixed
interface RuntimeConfig {
  // Authentication & OIDC
  VITE_AUTH_SERVICE_URL?: string
  VITE_LOGIN_URL?: string
  VITE_OIDC_CLIENT_ID?: string
  VITE_OIDC_REDIRECT_URI?: string
  VITE_OIDC_SCOPE?: string
  VITE_OIDC_RESPONSE_TYPE?: string

  // Platform URLs
  VITE_ADMIN_URL?: string

  // API Endpoints
  VITE_API_URL?: string
  // VITE_WS_URL removed (F4) — chat-service /v1/chat/ws was deleted.
  // Streaming runs through VITE_STREAMING_WS_URL → streaming-service.

  // LangGraph Services
  VITE_LANGGRAPH_HOST_URL?: string
  VITE_CHAT_API_URL?: string
  VITE_FILE_UPLOAD_URL?: string

  // Voice/WebRTC
  VITE_VOICE_API_URL?: string

  // Platform Zones
  VITE_EXECUTION_ZONE_URL?: string
  VITE_COORDINATION_ZONE_URL?: string
  VITE_GOVERNANCE_ZONE_URL?: string
  VITE_DATA_ZONE_URL?: string

  // Storage & Files
  VITE_STORAGE_URL?: string

  // Streaming Service
  VITE_STREAMING_WS_URL?: string

  // Feature Flags
  VITE_ENABLE_WEBSOCKET?: string
  VITE_ENABLE_FILE_UPLOAD?: string
  VITE_ENABLE_CHARTS?: string
  VITE_ENABLE_ANALYTICS?: string
  VITE_ENABLE_OPERATOR_TAKEOVER?: string
  VITE_DEBUG?: string

  // Password Protection (optional)
  VITE_ACCESS_PASSWORD?: string

  // Build Configuration
  VITE_ENVIRONMENT?: string
  VITE_APP_VERSION?: string
  VITE_APP_NAME?: string

  // Development
  DEV?: string
  NODE_ENV?: string
}

// Extend Window interface to include runtime config
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

/**
 * Get configuration value with runtime override support
 * Priority: window.__RUNTIME_CONFIG__ > import.meta.env > devDefault (dev mode only)
 *
 * CRITICAL: Production builds should NEVER have hardcoded domain fallbacks.
 * All URLs must come from runtime config or build-time env vars.
 */
function getConfig(
  key: keyof RuntimeConfig,
  devDefault?: string
): string | undefined {
  // Check runtime config first (set at container startup)
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key]
  if (runtimeValue !== undefined && runtimeValue !== '') {
    return runtimeValue
  }

  // Fall back to build-time env vars
  const envValue = import.meta.env[key]
  if (envValue !== undefined && envValue !== '') {
    return String(envValue)
  }

  // Use devDefault ONLY in development mode
  if (import.meta.env.DEV && devDefault) {
    return devDefault
  }

  // Return undefined if no config found
  return undefined
}

/**
 * Get boolean configuration value
 */
function getBooleanConfig(
  key: keyof RuntimeConfig,
  defaultValue: boolean
): boolean {
  const value = getConfig(key, String(defaultValue))
  if (typeof value === 'boolean') {
    return value
  }
  return value === 'true' || value === '1'
}

/**
 * Centralized configuration object
 * All env vars use VITE_ prefix for Vite compatibility
 *
 * CRITICAL: No hardcoded production domains!
 * - Development mode: Uses localhost defaults
 * - Production mode: REQUIRES runtime config or build-time env vars
 */
export const config = {
  // Authentication & OIDC
  authServiceUrl: getConfig('VITE_AUTH_SERVICE_URL') || (import.meta.env.DEV ? 'http://localhost:8081' : ''),
  loginUrl: getConfig('VITE_LOGIN_URL') || (import.meta.env.DEV ? 'http://localhost:3002' : ''),
  oidcClientId: getConfig('VITE_OIDC_CLIENT_ID') || 'chat-ui',
  oidcRedirectUri: getConfig('VITE_OIDC_REDIRECT_URI') || `${window.location.origin}/auth/callback`,
  oidcScope: getConfig('VITE_OIDC_SCOPE') || 'openid profile email',
  oidcResponseType: getConfig('VITE_OIDC_RESPONSE_TYPE') || 'code',

  // Platform URLs
  adminUrl: getConfig('VITE_ADMIN_URL') || (import.meta.env.DEV ? 'http://localhost:3001' : ''),

  // API Endpoints - always provide string defaults for services that require them
  apiUrl: getConfig('VITE_API_URL', 'http://localhost:8080') || 'http://localhost:8080',
  // wsUrl removed (F4) — use streamingWsUrl below. The chat-service WebSocket
  // (/v1/chat/ws) was deleted in Agion commit a1d0c17512 and is unreachable.

  // LangGraph Services
  langGraphHostUrl: getConfig('VITE_LANGGRAPH_HOST_URL') || '',
  chatApiUrl: getConfig('VITE_CHAT_API_URL') || '',
  fileUploadUrl: getConfig('VITE_FILE_UPLOAD_URL') || '',

  // Voice/WebRTC - defaults to apiUrl if not set
  voiceApiUrl: getConfig('VITE_VOICE_API_URL') || '',

  // Platform Zones
  executionZoneUrl: getConfig('VITE_EXECUTION_ZONE_URL') || '',
  coordinationZoneUrl: getConfig('VITE_COORDINATION_ZONE_URL') || '',
  governanceZoneUrl: getConfig('VITE_GOVERNANCE_ZONE_URL') || '',
  dataZoneUrl: getConfig('VITE_DATA_ZONE_URL') || '',

  // Storage & Files
  storageUrl: getConfig('VITE_STORAGE_URL') || '',

  // Streaming Service
  streamingWsUrl: getConfig('VITE_STREAMING_WS_URL') || '',

  // Feature Flags
  enableWebSocket: getBooleanConfig('VITE_ENABLE_WEBSOCKET', true),
  enableFileUpload: getBooleanConfig('VITE_ENABLE_FILE_UPLOAD', true),
  enableCharts: getBooleanConfig('VITE_ENABLE_CHARTS', true),
  enableAnalytics: getBooleanConfig('VITE_ENABLE_ANALYTICS', true),
  debug: getBooleanConfig('VITE_DEBUG', false),

  // Password Protection (optional)
  accessPassword: getConfig('VITE_ACCESS_PASSWORD') || '',

  // Build Configuration
  environment: (getConfig('VITE_ENVIRONMENT') || 'production') as 'development' | 'staging' | 'production',
  appVersion: getConfig('VITE_APP_VERSION') || '1.0.0',
  appName: getConfig('VITE_APP_NAME') || 'Agion Chat',

  // Development flags
  isDev: getBooleanConfig('DEV', import.meta.env.DEV || false),
  nodeEnv: getConfig('NODE_ENV') || import.meta.env.MODE || 'production',
}

/**
 * Log configuration on initialization (only non-sensitive values)
 */
if (typeof window !== 'undefined' && config.debug) {
  console.log('[Config] Runtime configuration loaded:', {
    authServiceUrl: config.authServiceUrl,
    apiUrl: config.apiUrl,
    streamingWsUrl: config.streamingWsUrl,
    environment: config.environment,
    isDev: config.isDev,
    usingRuntimeConfig: !!window.__RUNTIME_CONFIG__,
  })
}

/**
 * Whether operator conversation takeover shows (agion/Agion#3435): Take over
 * in Escalations, operator mode in a run's conversation, and a person's
 * replies and hold notices. Off by default. Read at each call, so a runtime
 * config set after this module loaded counts.
 */
export const operatorTakeoverEnabled = (): boolean =>
  getBooleanConfig('VITE_ENABLE_OPERATOR_TAKEOVER', false)

/**
 * Export individual getters for backward compatibility
 */
export const getAuthServiceUrl = () => config.authServiceUrl
export const getLoginUrl = () => config.loginUrl
export const getApiUrl = () => config.apiUrl
export const getOidcConfig = () => ({
  authServiceUrl: config.authServiceUrl,
  authUrl: config.authServiceUrl,
  loginUrl: config.loginUrl,
  apiUrl: config.apiUrl,
  clientId: config.oidcClientId,
  redirectUri: config.oidcRedirectUri,
  scope: config.oidcScope,
  responseType: config.oidcResponseType,
})
